import config from "@app/config";
import axios from "axios";

import { addToBackInStock, addToCart } from "@app/scripts/utils";

import type { Product, RecentlyViewed, Variant } from "@app/scripts/types";

class ProductDetails extends HTMLElement {
  product: Product;
  productForm: HTMLFormElement | null;
  productBISForm: HTMLFormElement | null;
  addToCartWrapper: HTMLButtonElement | null;
  sizeText: HTMLElement | null;
  errorMessage: HTMLElement | null;
  stockLevels: { title: string; level: "regular_stock" | "low_stock" | "out_of_stock" }[];
  isGiftCard: Boolean;
  selectSizeGuideBtns: NodeListOf<HTMLElement>;
  giftCardProducts?: Product[];
  qtyInput?: HTMLElement | null;

  constructor() {
    super();
    this.product = JSON.parse(this.querySelector("[data-json]")?.innerText);
    this.stockLevels = JSON.parse(this.querySelector("[data-productstock]")?.innerText)?.map((x: string) => ({
      title: x.split(":")[0],
      level: x.split(":")[1],
    }));
    this.productForm = this.querySelector(".product__form");
    this.productBISForm = this.querySelector(".product__bis-form");
    this.addToCartWrapper = this.querySelector(".product__add-to-cart");
    this.sizeText = this.querySelector("[data-sizetext]");
    this.qtyInput = this.querySelector("[data-qtyinput]");
    this.errorMessage = this.querySelector("[data-error-message]");
    this.isGiftCard = JSON.parse(this.querySelector("[data-productisgiftcard]")?.innerText || "{}")?.isGiftCard;
    this.selectSizeGuideBtns = this.querySelectorAll("[data-opensizeguide]");

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleBISSubmit = this.handleBISSubmit.bind(this);
    this.handleScrollToSizeGuide = this.handleScrollToSizeGuide.bind(this);
  }

  connectedCallback() {
    const is_quick_add = this.getAttribute("quick-add") === "true";
    if (!is_quick_add) {
      this.addToRecentlyViewed();
    }

    this.productForm?.addEventListener("submit", this.handleAddToCart);
    this.productBISForm?.addEventListener("submit", this.handleBISSubmit);

    this.selectSizeGuideBtns?.forEach((btn) => btn.addEventListener("click", this.handleScrollToSizeGuide));

    this.productForm
      ?.querySelectorAll(".product__variant-buttons input[type=radio]")
      ?.forEach((input) => input.addEventListener("change", this.handleVariantChange));

    this.variantChange(this.productForm?.querySelector(".product__variant-buttons input[type=radio]:checked"));

    if (this.isGiftCard) {
      axios
        .get("/collections/gift-cards?json=true")
        .then((res) => {
          this.giftCardProducts = res.data;
          this.giftVariantChange();
        })
        .catch((err) => console.error(err));
    }
  }

  async handleBISSubmit(e: SubmitEvent) {
    e.preventDefault();
    const data = new FormData(this.productBISForm);
    const email = data.get("email") as string;
    const variantId = parseInt(data?.get("variant") as string);
    const res = await addToBackInStock({ email, variantId });
  }

  async handleAddToCart(e: SubmitEvent) {
    e.preventDefault();
    const data = new FormData(this.productForm);
    const selectedId = data.get("id") as string;

    this.errorMessage?.classList.add("hidden");

    if (!selectedId) return;

    const res = await addToCart({
      id: parseInt(selectedId),
      qty: this.qtyInput?.value || 1,
    });

    if (res.status === 200) {
      return false;
    } else {
      if (this.errorMessage) {
        this.errorMessage.innerHTML = res?.description ?? "There was an error adding to the cart.";
        this.errorMessage.classList.remove("hidden");
      }
    }
  }

  handleScrollToSizeGuide(e: Event) {
    e?.preventDefault();
    const sizeGuideBtn = this.querySelector("[data-slidetoggle='#box-size_guide']");
    sizeGuideBtn?.click();
    sizeGuideBtn?.scrollIntoView({
      behavior: "smooth",
    });
  }

  handleVariantChange(e: Event) {
    const target = e.target instanceof HTMLInputElement ? e.target : null;
    this.isGiftCard ? this.giftVariantChange(e) : this.variantChange(target);
  }

  variantChange(target: HTMLInputElement | null) {
    const hiddenId = this.querySelector("input[name=id]") as HTMLInputElement;
    const hiddenBisId = this.productBISForm?.querySelector("input[name=variant]") as HTMLInputElement;
    if (!hiddenId || !hiddenBisId) return;

    const oosDetails = this.querySelector(".product_oos") as HTMLInputElement;
    const productQty = this.querySelector(".product__qty") as HTMLInputElement;
    let selectedVariant: any;

    if (!target) {
      selectedVariant =
        this.product?.variants?.find((variant: any) => variant.id == hiddenId.value) || this.product?.variants?.[0];
    } else {
      const targetVariantId = target.getAttribute("data-variant-id");

      if (target.disabled || !targetVariantId) return;

      selectedVariant = this.product?.variants?.find((variant: any) => variant.id == targetVariantId);
    }

    if (!selectedVariant) return;

    const isOos = target?.closest(".button")?.classList.contains("oos") || selectedVariant.available === false;
    const selectedStockLevel = this.stockLevels.find((lvl) => lvl.title === selectedVariant.title)?.level ?? null;

    if (target && this.sizeText) {
      this.sizeText.innerHTML = `<strong>Size:</strong> <span>${target.value}</span>`;
    }

    hiddenId.value = selectedVariant.id.toString();
    hiddenBisId.value = selectedVariant.id.toString();

    this.errorMessage?.classList.add("hidden");
    this.querySelector(".product__low-stock")?.classList.add("hidden");
    this.querySelector(".product__out-of-stock")?.classList.add("hidden");

    if (selectedStockLevel === "low_stock") {
      this.querySelector(".product__low-stock")?.classList.remove("hidden");
    }
    if (selectedStockLevel === "out_of_stock") {
      this.querySelector(".product__out-of-stock")?.classList.remove("hidden");
    }

    if (isOos) {
      oosDetails?.classList.remove("hidden");
      productQty?.classList.add("hidden");
      this.addToCartWrapper?.classList.add("hidden");
      (this.querySelector(".product__bis-form-email input") as HTMLInputElement).focus();
    } else {
      oosDetails?.classList.add("hidden");
      productQty?.classList.remove("hidden");
      this.addToCartWrapper?.classList.remove("hidden");
    }

    window.dispatchEvent(new CustomEvent("variantchange", { detail: selectedVariant.id }));
  }

  giftVariantChange() {
    const hiddenId = this.querySelector("input[name=id]") as HTMLInputElement;
    const type = this.querySelector("[name='giftcard_type']:checked");
    const typeValue: string = type
      ? type?.value
      : this.product?.title?.toLowerCase()?.includes("digital")
      ? "digital"
      : "physical";

    const productVariantBtns = this.productForm?.querySelector(".product__variant-buttons");
    const variantId = productVariantBtns?.querySelector("[name='variant_id']:checked");
    const lastSelectedPrice = parseInt(variantId?.dataset.price);

    const buildChips = (variants?: Variant[]) => {
      return (
        variants
          ?.map(
            (variant) => /* HTML*/ `<label class="button button--chip">
              <input type="radio" value="${variant.id}" name="variant_id" data-price="${variant.price}" ${
              lastSelectedPrice === variant.price ? "checked" : ""
            }>
              <span>$${variant.price / 100}</span>
            </label>`
          )
          .join("") ?? ""
      );
    };

    if (!productVariantBtns || !this.productForm) return;

    if (typeValue === "digital") {
      const variants = this.giftCardProducts?.find((product) =>
        product?.title?.toLowerCase()?.includes("digital")
      )?.variants;
      productVariantBtns.innerHTML = buildChips(variants);
    } else if (typeValue === "physical") {
      const variants = this.giftCardProducts
        ?.filter((product) => !product?.title?.toLowerCase()?.includes("digital"))
        ?.sort((a, b) => a.price - b.price)
        ?.map((product) => product.variants?.[0]);
      productVariantBtns.innerHTML = buildChips(variants);
    }

    // re-attach listeners
    this.productForm?.querySelectorAll(".product__variant-buttons input[type=radio]")?.forEach((input) => {
      input.removeEventListener("change", this.handleVariantChange);
      input.addEventListener("change", this.handleVariantChange);
    });

    // check the first option if nothing matches
    if (!productVariantBtns?.querySelector("[name='variant_id']:checked")) {
      productVariantBtns.querySelector("[name='variant_id']")?.setAttribute("checked", "true");
    }

    this.querySelector(`[value='${typeValue}']`)?.setAttribute("checked", "true");

    if (!hiddenId) {
      return;
    }

    const finalValue = productVariantBtns?.querySelector("[name='variant_id']:checked")?.value;

    if (finalValue) hiddenId.value = finalValue;
  }

  /**
   * Add the product handle to the recently viewed localstorage obj.
   */
  addToRecentlyViewed() {
    let recently_viewed: RecentlyViewed[] = JSON.parse(localStorage.getItem(config.keys.recently_viewed) ?? "[]");
    const index = recently_viewed.findIndex(({ id }) => id === this.product.id);
    const entry = {
      id: this.product.id,
      time: parseInt(Date.now().toString()),
    };
    index !== -1 ? (recently_viewed[index] = entry) : recently_viewed.push(entry);

    recently_viewed.sort((a, b) => b.time - a.time);

    if (recently_viewed.length >= 10) {
      recently_viewed = recently_viewed.slice(0, 10);
    }

    localStorage.setItem(config.keys.recently_viewed, JSON.stringify(recently_viewed));
  }
}

customElements.define("product-details", ProductDetails);
